import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { crmData } from '../data/crmData';

export const Projects = () => {
  const [activeTab, setActiveTab] = useState('projects'); // 'clients', 'projects', 'reports'
  const [searchTerm, setSearchTerm] = useState('');

  const filteredClients = useMemo(() => 
    crmData.clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.contact.toLowerCase().includes(searchTerm.toLowerCase())),
    [searchTerm]
  );

  const filteredProjects = useMemo(() => 
    crmData.projects.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [searchTerm]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Consultant CRM</h1>
          <p className="text-slate-500 mt-1">Sentral styring av kunder, prosjekter og leveranser.</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-xl">
          <button 
            onClick={() => setActiveTab('projects')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'projects' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Prosjekter
          </button>
          <button 
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'clients' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Kundebase
          </button>
          <button 
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'reports' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Rapportering
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Aktive Prosjekter', val: '12', icon: 'Briefcase', color: 'indigo' },
          { label: 'Kritiske Saker', val: '2', icon: 'AlertTriangle', color: 'rose' },
          { label: 'Fullførte Kartlegginger', val: '450+', icon: 'CheckCircle', color: 'emerald' },
          { label: 'Planlagte Workshops', val: '8', icon: 'Calendar', color: 'amber' },
        ].map((stat, i) => (
          <Card key={i} className="bg-white">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-${stat.color}-50 text-${stat.color}-600`}>
                <SafeIcon name={stat.icon} />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
                <p className="text-xl font-black text-slate-900">{stat.val}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Areas */}
      <div className="space-y-6">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <SafeIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder={activeTab === 'clients' ? "Søk i kunder..." : "Søk i prosjekter..."}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="primary" icon={<SafeIcon name="Plus" />}>
            {activeTab === 'clients' ? "Ny Kunde" : "Nytt Prosjekt"}
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'projects' && (
            <motion.div key="projects" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 font-bold text-slate-600">Prosjektnavn</th>
                        <th className="px-6 py-4 font-bold text-slate-600">Type</th>
                        <th className="px-6 py-4 font-bold text-slate-600">Fremdrift</th>
                        <th className="px-6 py-4 font-bold text-slate-600">Svar / Mål</th>
                        <th className="px-6 py-4 font-bold text-slate-600">Status</th>
                        <th className="px-6 py-4 text-right">Handling</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredProjects.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50/50 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-900">{p.name}</div>
                            <div className="text-[10px] text-slate-400 font-medium">Kunde: {crmData.clients.find(c => c.id === p.clientId)?.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">
                              {p.type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3 w-32">
                              <div className="flex-1 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-indigo-600 h-full" style={{ width: `${p.progress}%` }} />
                              </div>
                              <span className="text-[10px] font-bold text-slate-500">{p.progress}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-500 font-medium">
                            {p.assessments ? `${p.assessments.completed}/${p.assessments.total}` : `${p.workshops} workshops`}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                              p.status === 'Kritisk' ? 'bg-rose-100 text-rose-700' : 
                              p.status === 'Pågår' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {p.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button variant="ghost" size="sm" icon={<SafeIcon name="MoreHorizontal" />} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </motion.div>
          )}

          {activeTab === 'clients' && (
            <motion.div key="clients" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client) => (
                <Card key={client.id} hover={true} className="group">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                        <SafeIcon name="Hexagon" className="text-xl" />
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase">Helse-index</p>
                        <p className={`text-lg font-black ${client.healthScore < 50 ? 'text-rose-600' : 'text-emerald-600'}`}>{client.healthScore}%</p>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{client.name}</h3>
                    <p className="text-xs text-slate-500 mb-4">{client.industry} • {client.contact}</p>
                    
                    <div className="grid grid-cols-2 gap-4 py-4 border-t border-slate-50 mb-4">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Prosjekter</p>
                        <p className="text-sm font-bold text-slate-700">{client.projectsCount} aktive</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Siste aktivitet</p>
                        <p className="text-sm font-bold text-slate-700">{client.lastActivity}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" className="flex-1">Gå til Kunde</Button>
                      <Button variant="ghost" size="sm" icon={<SafeIcon name="Mail" />} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          )}

          {activeTab === 'reports' && (
            <motion.div key="reports" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Card>
                <CardHeader title="Rapportgenerator" subtitle="Generer profesjonelle innsiktsrapporter for dine kunder." />
                <CardContent className="p-12 text-center space-y-6">
                  <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto">
                    <SafeIcon name="FileText" className="text-4xl" />
                  </div>
                  <div className="max-w-md mx-auto">
                    <h3 className="text-xl font-bold text-slate-900">Klar for PDF-generering</h3>
                    <p className="text-sm text-slate-500 mt-2">
                      Velg et prosjekt fra listen for å aggregere data, AI-tolkninger og anbefalinger til en ferdig rapport.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg mx-auto">
                    <select className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm">
                      <option>Velg prosjekt...</option>
                      {crmData.projects.map(p => <option key={p.id}>{p.name}</option>)}
                    </select>
                    <Button variant="primary" icon={<SafeIcon name="Download" />}>Generer Rapport</Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};